import { saveWaitingListEntry } from "./waitingListClient";
import { createEndpointCall } from "./endpointClient";
import { DocumentData, DocumentReference, getDoc } from "firebase/firestore";
import { httpsCallable, HttpsCallableResult } from "firebase/functions";
import {
  getValue,
  fetchAndActivate,
  setCustomSignals,
} from "firebase/remote-config";
import {
  app,
  auth,
  db,
  storage,
  functions,
  analytics,
  remoteConfig,
  remoteConfigInitialization,
  isEmulator,
  isProduction,
  firebaseConfig,
  googleProvider,
  appleProvider,
} from "./firebaseCore";
import { currentConfig } from "./appConfig";
import {
  AddSharedAnimalRequest,
  AppointAmbassadorRequest,
  AppointAmbassadorResponse,
  RevokeAmbassadorRequest,
  RevokeAmbassadorResponse,
  AnalyzeDocumentRequest,
  AnalyzeDocumentResponse,
  RoutineRewardGoal,
  StartRoutineRewardRequest,
  ClaimRoutineRewardRequest,
  ChangeOutfitRequest,
  ChangeOutfitResponse,
  AdminEquipItemRequest,
  AdminEquipItemResponse,
  EditPersonaRequest,
  ChangeUserRoleRequest,
  ChangeUserRoleResponseMap,
  GenerateInsightsSummaryRequest,
  GenerateInsightsSummaryResponse,
  GetAzureOpenAITokenRequest,
  GetAzureOpenAITokenResponse,
  GetRemainingTopicsRequest,
  GetRemainingTopicsResponse,
  UploadImageRequest,
  UploadImageResponse,
  ManageJobRequest,
  ManageJobResponse,
  FreeAccessRequest,
  FreeAccessResponse,
  WithRef,
  withRefOrUndefined,
  ChatRequest,
  ChatChunk,
  ChatResponse,
  FunFactsRequest,
  FunFactsResponse,
  PersonalityQuestionsRequest,
  PersonalityQuestionsResponse,
  ApplyPersonalityHintsRequest,
  ApplyPersonalityHintsResponse,
  DeleteCharacterRequest,
  DeleteCharacterResponse,
  ReassignJobAgentRequest,
  ReassignJobAgentResponse,
  ClinicianRequest,
  ClinicianResponseMap,
  PurchasesRequest,
  PurchasesResponseMap,
  TestManagerRequest,
  TestManagerResponseMap,
  ManageRequest,
  ManageResponseMap,
  setFirestoreContractProvider,
  GenerateBlogContentRequest,
  GenerateBlogContentResponse,
  RefineBlogContentRequest,
  RefineBlogContentResponse,
  GenerateBlogIdeasRequest,
  GenerateBlogIdeasResponse,
  PublishBlogRequest,
  PublishBlogResponse,
  AddWaitingListEntryRequest,
  AddRaffleEntryRequest,
  AddRaffleEntryResponse,
} from "@um/contracts";
import { getCollection, getDocument } from "./firestore";
import { getEnv } from "./env";

const config = currentConfig();

export {
  app,
  auth,
  db,
  storage,
  functions,
  analytics,
  remoteConfig,
  isEmulator,
  isProduction,
  firebaseConfig,
  googleProvider,
  appleProvider,
};

function compactRemoteConfigDefaults(
  defaults: Record<string, string | number | boolean | undefined>
): Record<string, string | number | boolean> {
  return Object.fromEntries(
    Object.entries(defaults).filter(([, value]) => value !== undefined)
  ) as Record<string, string | number | boolean>;
}

/**
 * Result of the one-shot `fetchAndActivate(remoteConfig)` performed at app start.
 *
 * Resolves (never rejects) so consumers can safely `.then()` to re-read values
 * once the network round-trip completes. When Remote Config is disabled or the
 * call errors, `activated` is `false` and `error` may be populated.
 */
export type RemoteConfigActivationStatus = {
  activated: boolean;
  durationMs: number;
  error?: unknown;
};

let resolveRemoteConfigReady: (
  status: RemoteConfigActivationStatus
) => void = () => {};

/**
 * Resolves once the startup `fetchAndActivate` call settles (success or failure).
 *
 * Use this anywhere you need values that may not be ready at first read — most
 * importantly anything tied to an A/B experiment variant. The promise never
 * rejects; inspect `status.activated` / `status.error` instead.
 */
export const remoteConfigReady: Promise<RemoteConfigActivationStatus> =
  new Promise((resolve) => {
    resolveRemoteConfigReady = resolve;
  });

// ─── QA variant override ────────────────────────────────────────────────────

/**
 * localStorage key holding a QA variant override (e.g. `"juicy"` | `"control"`).
 * Read before the Remote Config fetch and sent as the `qa_group` custom signal
 * (web SDK) so the server-side `qa_force_*` template conditions serve the
 * forced variant. Devices with an active override are excluded from experiment
 * assignment stamping — see `services/experiments.ts` in the app.
 */
export const QA_GROUP_STORAGE_KEY = "um.qaGroup";

/** Current QA variant override, or null when none is set. */
export function getQaGroupOverride(): string | null {
  try {
    return window.localStorage.getItem(QA_GROUP_STORAGE_KEY)?.trim() || null;
  } catch {
    return null;
  }
}

// ─── DEV runtime config overrides ───────────────────────────────────────────

/**
 * localStorage key holding DEV-only Remote Config overrides as a JSON object
 * (key → raw string value). Set from the dev settings dialog; consulted first
 * by `getRemoteConfigValue`. Never read in production builds.
 */
export const DEV_CONFIG_OVERRIDES_STORAGE_KEY = "um.devConfigOverrides";

/** All DEV runtime Remote Config overrides ({} outside DEV builds). */
export function getDevConfigOverrides(): Record<string, string> {
  if (!import.meta.env.DEV) return {};
  try {
    const raw = window.localStorage.getItem(DEV_CONFIG_OVERRIDES_STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object"
      ? (parsed as Record<string, string>)
      : {};
  } catch {
    return {};
  }
}

function getDevConfigOverride(key: string): string | undefined {
  const value = getDevConfigOverrides()[key];
  return typeof value === "string" && value !== "" ? value : undefined;
}

/**
 * Set (or clear with null/"") a DEV runtime override for a Remote Config key.
 * Values are stored as raw strings and coerced to the key's default type on
 * read, same as `VITE_CONFIG_<KEY>` env overrides.
 */
export function setDevConfigOverride(key: string, value: string | null): void {
  if (!import.meta.env.DEV) return;
  const overrides = getDevConfigOverrides();
  if (value == null || value === "") {
    delete overrides[key];
  } else {
    overrides[key] = value;
  }
  try {
    if (Object.keys(overrides).length === 0) {
      window.localStorage.removeItem(DEV_CONFIG_OVERRIDES_STORAGE_KEY);
    } else {
      window.localStorage.setItem(
        DEV_CONFIG_OVERRIDES_STORAGE_KEY,
        JSON.stringify(overrides)
      );
    }
  } catch {
    // localStorage unavailable — override simply won't persist
  }
}

// ─── Remote Config backends ─────────────────────────────────────────────────
//
// Two backends behind the same synchronous read API:
// - web: `firebase/remote-config` JS SDK. Presents the WEB app identity.
// - native: `config.nativeRemoteConfig` (Capacitor plugin). Presents the
//   ANDROID/iOS app identity — required for Firebase A/B experiments to join
//   variant exposure with native Analytics goal events.
//
// Values fetched by the native backend are cached below after activation so
// `getRemoteConfigValue` / `getRemoteConfigValueSource` stay synchronous.
// Reads before the cache fills return the JS defaults with source "default" —
// identical to the web SDK's pre-activation window.

type NativeRemoteConfigEntry = {
  value: string;
  source: "remote" | "default" | "static";
};
const nativeRemoteConfigValues = new Map<string, NativeRemoteConfigEntry>();
const nativeRemoteConfigBackend = config.isNativePlatform
  ? config.nativeRemoteConfig
  : undefined;
const usingNativeRemoteConfig = !!(
  config.remoteConfig && nativeRemoteConfigBackend
);

async function initializeRemoteConfigValues() {
  await remoteConfigInitialization;
  if (config.remoteConfig && nativeRemoteConfigBackend) {
    const backend = nativeRemoteConfigBackend;
    const declaredKeys = Object.keys(config.remoteConfig);
    const startedAt = Date.now();
    const qaGroup = getQaGroupOverride();
    console.log(
      `[RemoteConfig] native fetchAndActivate start — keys=[${declaredKeys.join(", ")}]${qaGroup ? ` qaOverride=${qaGroup}` : ""}`
    );
    // Reads the native SDK's current values into the synchronous cache. Only
    // remote-sourced entries are stored — empty static reads must not shadow
    // the JS defaults. Runs twice: before the fetch (the native SDK persists
    // the last ACTIVATED config on disk, so a returning user gets last
    // session's variant immediately, matching the web SDK's warm cache) and
    // after (fresh values — or, when the fetch failed, still the persisted
    // ones, which must survive: falling back to JS defaults here would flip a
    // stamped juicy user to control for the whole session).
    const fillCacheFromBackend = async (phase: string) => {
      for (const key of declaredKeys) {
        try {
          const entry = await backend.getValue(key);
          if (entry.source === "remote") {
            nativeRemoteConfigValues.set(key, entry);
            const printable =
              entry.value.length > 120
                ? `${entry.value.slice(0, 117)}...`
                : entry.value;
            console.log(
              `[RemoteConfig] value (${phase}) — key=${key} source=${entry.source} value=${JSON.stringify(printable)}`
            );
          }
        } catch (e) {
          console.warn(`[RemoteConfig] failed to read value for ${key}`, e);
        }
      }
    };
    (async () => {
      await fillCacheFromBackend("persisted");
      if (qaGroup) {
        // Forced values must arrive promptly; the default fetch interval is 12h.
        try {
          await backend.setSettings?.({ minimumFetchIntervalInSeconds: 60 });
        } catch (e) {
          console.warn("[RemoteConfig] native setSettings failed", e);
        }
      }
      let fetchError: unknown;
      try {
        await backend.fetchAndActivate();
      } catch (e) {
        fetchError = e;
      }
      await fillCacheFromBackend(
        fetchError ? "persisted-after-failed-fetch" : "fetched"
      );
      const durationMs = Date.now() - startedAt;
      if (fetchError) {
        console.warn(
          `[RemoteConfig] native fetchAndActivate failed — durationMs=${durationMs}`,
          fetchError
        );
        resolveRemoteConfigReady({
          activated: false,
          durationMs,
          error: fetchError,
        });
      } else {
        console.log(
          `[RemoteConfig] native fetchAndActivate done — activated=true durationMs=${durationMs}`
        );
        resolveRemoteConfigReady({ activated: true, durationMs });
      }
    })().catch((error) => {
      const durationMs = Date.now() - startedAt;
      console.warn(
        `[RemoteConfig] native boot failed — durationMs=${durationMs}`,
        error
      );
      resolveRemoteConfigReady({ activated: false, durationMs, error });
    });
  } else if (config.remoteConfig && remoteConfig) {
    remoteConfig.defaultConfig = compactRemoteConfigDefaults(
      config.remoteConfig
    );
    const declaredKeys = Object.keys(config.remoteConfig);
    const startedAt = Date.now();
    const qaGroup = getQaGroupOverride();
    if (qaGroup) {
      // Forced values must arrive promptly; the web SDK default fetch interval is 12h.
      remoteConfig.settings.minimumFetchIntervalMillis = 60_000;
    }
    console.log(
      `[RemoteConfig] fetchAndActivate start — keys=[${declaredKeys.join(", ")}] defaults=${Object.keys(remoteConfig.defaultConfig).length}${qaGroup ? ` qaOverride=${qaGroup}` : ""}`
    );
    // Custom signals must be set BEFORE the fetch — the server evaluates the
    // qa_force_* / platform template conditions against the signals sent with
    // the request. Signals persist locally across sessions.
    setCustomSignals(remoteConfig, {
      platform: config.getPlatform?.() ?? "web",
      ...(qaGroup ? { qa_group: qaGroup } : {}),
    })
      .catch((e) => console.warn("[RemoteConfig] setCustomSignals failed", e))
      .then(() => fetchAndActivate(remoteConfig!))
      .then((activated) => {
        const durationMs = Date.now() - startedAt;
        console.log(
          `[RemoteConfig] fetchAndActivate done — activated=${activated} durationMs=${durationMs}`
        );
        for (const key of declaredKeys) {
          try {
            const v = getValue(remoteConfig!, key);
            // Trim long values so we don't dump JSON blobs into the console.
            const asString = v.asString();
            const printable =
              asString.length > 120 ? `${asString.slice(0, 117)}...` : asString;
            console.log(
              `[RemoteConfig] value — key=${key} source=${v.getSource()} value=${JSON.stringify(printable)}`
            );
          } catch (e) {
            console.warn(`[RemoteConfig] failed to read value for ${key}`, e);
          }
        }
        resolveRemoteConfigReady({ activated, durationMs });
      })
      .catch((error) => {
        const durationMs = Date.now() - startedAt;
        console.warn(
          `[RemoteConfig] fetchAndActivate failed — durationMs=${durationMs}`,
          error
        );
        resolveRemoteConfigReady({ activated: false, durationMs, error });
      });
  } else {
    resolveRemoteConfigReady({ activated: false, durationMs: 0 });
  }
}
void initializeRemoteConfigValues().catch((error) => {
  resolveRemoteConfigReady({ activated: false, durationMs: 0, error });
});

/**
 * Updates Remote Config custom signals (web SDK only — the native plugin has no
 * custom-signal support; native QA forcing uses the `qa_group` GA user property
 * instead). Signals persist across sessions and are evaluated server-side on
 * the next fetch.
 */
export async function setRemoteConfigCustomSignals(
  signals: Record<string, string | number | null>
): Promise<void> {
  if (!remoteConfig || usingNativeRemoteConfig) return;
  try {
    await setCustomSignals(remoteConfig, signals);
  } catch (e) {
    console.warn("[RemoteConfig] setCustomSignals failed", e);
  }
}

type RemoteConfigDefaults = NonNullable<typeof config.remoteConfig>;

/**
 * Where a Remote Config value resolved from when read. Mirrors the underlying
 * Firebase `Value.getSource()` return values, plus a couple of app-level cases:
 *
 * - `remote`: fetched from the Firebase backend and activated.
 * - `default`: in-app default registered via `remoteConfig.defaultConfig`.
 * - `static`: SDK returned the empty value (no remote, no default registered).
 * - `dev_override`: DEV-only runtime override from the dev settings dialog.
 * - `env_override`: DEV-only `VITE_CONFIG_<KEY>` env var beat Remote Config.
 * - `absent`: Remote Config not configured for this app at all.
 */
export type RemoteConfigValueSource =
  | "remote"
  | "default"
  | "static"
  | "dev_override"
  | "env_override"
  | "absent";

/**
 * Returns where the current `getRemoteConfigValue(key)` result came from.
 * Useful for telemetry / debugging experiment delivery.
 */
export function getRemoteConfigValueSource<
  K extends keyof RemoteConfigDefaults,
>(key: K): RemoteConfigValueSource {
  if (import.meta.env.DEV && getDevConfigOverride(key as string)) {
    return "dev_override";
  }
  if (
    import.meta.env.DEV &&
    getEnv(`CONFIG_${(key as string).toUpperCase()}`)
  ) {
    return "env_override";
  }
  if (usingNativeRemoteConfig) {
    // Non-remote native sources (pre-activation, static, native default) all
    // resolve to the JS default — report them as "default".
    const entry = nativeRemoteConfigValues.get(key as string);
    return entry?.source === "remote" ? "remote" : "default";
  }
  if (!remoteConfig) return config.remoteConfig ? "default" : "absent";
  try {
    return getValue(remoteConfig!, key as string).getSource();
  } catch {
    return "absent";
  }
}

export function getRemoteConfigValue<K extends keyof RemoteConfigDefaults>(
  key: K
): RemoteConfigDefaults[K] {
  const defaultVal = config.remoteConfig![key];

  // DEV-only: runtime override from the dev settings dialog, then
  // VITE_CONFIG_<KEY>, override Remote Config (never in production builds).
  const override =
    import.meta.env.DEV &&
    (getDevConfigOverride(key as string) ||
      getEnv(`CONFIG_${(key as string).toUpperCase()}`));
  if (override) {
    if (typeof defaultVal === "number")
      return parseInt(override) as unknown as RemoteConfigDefaults[K];
    if (typeof defaultVal === "boolean")
      return (override === "true") as unknown as RemoteConfigDefaults[K];
    return override as unknown as RemoteConfigDefaults[K];
  }

  if (usingNativeRemoteConfig) {
    const entry = nativeRemoteConfigValues.get(key as string);
    // Pre-activation, static, or native-default reads serve the JS default
    // (no native in-app defaults are registered) — same window the web SDK
    // has before activation.
    if (!entry || entry.source !== "remote") {
      return defaultVal as RemoteConfigDefaults[K];
    }
    const raw = entry.value;
    if (typeof defaultVal === "number") {
      const n = Number(raw);
      return (Number.isNaN(n) ? 0 : n) as unknown as RemoteConfigDefaults[K];
    }
    if (typeof defaultVal === "boolean") {
      // Mirror the web SDK's Value.asBoolean() truthy set.
      return ["1", "true", "t", "yes", "y", "on"].includes(
        raw.toLowerCase()
      ) as unknown as RemoteConfigDefaults[K];
    }
    if (defaultVal === undefined) {
      const value = raw.trim();
      return (value || undefined) as RemoteConfigDefaults[K];
    }
    return raw as unknown as RemoteConfigDefaults[K];
  }

  if (!remoteConfig) {
    return defaultVal as RemoteConfigDefaults[K];
  }

  const v = getValue(remoteConfig!, key as string);

  if (typeof defaultVal === "number")
    return v.asNumber() as unknown as RemoteConfigDefaults[K];
  if (typeof defaultVal === "boolean")
    return v.asBoolean() as unknown as RemoteConfigDefaults[K];
  if (defaultVal === undefined) {
    const value = v.asString().trim();
    return (value || undefined) as RemoteConfigDefaults[K];
  }
  return v.asString() as unknown as RemoteConfigDefaults[K];
}

// functions
export const getAzureOpenAIToken = httpsCallable<
  GetAzureOpenAITokenRequest,
  GetAzureOpenAITokenResponse
>(functions, "getAzureOpenAIToken");

export const analyzeDocument = createEndpointCall<
  AnalyzeDocumentRequest,
  AnalyzeDocumentResponse
>(functions, "analyzeDocument");
export const generateInsightsSummary = createEndpointCall<
  GenerateInsightsSummaryRequest,
  GenerateInsightsSummaryResponse
>(functions, "generateInsightsSummary");
export const getRemainingTopics = createEndpointCall<
  GetRemainingTopicsRequest,
  GetRemainingTopicsResponse
>(functions, "getRemainingTopics");

export const addSharedAnimal = createEndpointCall<AddSharedAnimalRequest>(
  functions,
  "addSharedAnimal"
);

export const appointAmbassadorFn = createEndpointCall<
  AppointAmbassadorRequest,
  AppointAmbassadorResponse
>(functions, "appointAmbassador");

export function appointAmbassador(
  request: AppointAmbassadorRequest
): Promise<HttpsCallableResult<AppointAmbassadorResponse>> {
  return appointAmbassadorFn(request);
}

export const revokeAmbassadorFn = createEndpointCall<
  RevokeAmbassadorRequest,
  RevokeAmbassadorResponse
>(functions, "revokeAmbassador");

export function revokeAmbassador(
  request: RevokeAmbassadorRequest
): Promise<HttpsCallableResult<RevokeAmbassadorResponse>> {
  return revokeAmbassadorFn(request);
}

// functions

const changeUserRoleFunction = createEndpointCall<
  ChangeUserRoleRequest,
  unknown
>(functions, "changeUserRole");

export function changeUserRole<T extends ChangeUserRoleRequest>(
  request: T
): Promise<HttpsCallableResult<ChangeUserRoleResponseMap[T["action"]]>> {
  return changeUserRoleFunction(request) as Promise<
    HttpsCallableResult<ChangeUserRoleResponseMap[T["action"]]>
  >;
}

// Timeout must cover the backend's full budget (timeoutSeconds: 180 — sharp
// processing + Gemini analysis); the SDK default of 70s made the client give
// up while the upload was still succeeding (UM-263/UM-265).
export const uploadImageFunction = createEndpointCall<
  UploadImageRequest,
  UploadImageResponse
>(functions, "uploadImage", { timeout: 180_000 });

export const manageJob = httpsCallable<ManageJobRequest, ManageJobResponse>(
  functions,
  "manageJob"
);

export const freeAccess = createEndpointCall<
  FreeAccessRequest,
  FreeAccessResponse
>(functions, "freeAccess");

export const generateBlogContent = createEndpointCall<
  GenerateBlogContentRequest,
  GenerateBlogContentResponse
>(functions, "generateBlogContent");

export const refineBlogContent = createEndpointCall<
  RefineBlogContentRequest,
  RefineBlogContentResponse
>(functions, "refineBlogContent");

export const generateBlogIdeas = createEndpointCall<
  GenerateBlogIdeasRequest,
  GenerateBlogIdeasResponse
>(functions, "generateBlogIdeas");

export const publishBlog = createEndpointCall<
  PublishBlogRequest,
  PublishBlogResponse
>(functions, "publishBlog");

export const addRaffleEntry = createEndpointCall<
  AddRaffleEntryRequest,
  AddRaffleEntryResponse
>(functions, "addRaffleEntry");

export async function getWithRef<T = DocumentData>(
  ref: DocumentReference<T> | string | WithRef<T>
): Promise<WithRef<T>> {
  if (typeof ref === "object" && "ref" in ref) return ref;

  const obj = await getWithRefOrUndefined(ref);
  if (!obj) {
    throw new Error(
      `Document ${typeof ref === "string" ? ref : ref.path} not found`
    );
  }
  return obj;
}

export async function getWithRefOrUndefined<T = DocumentData>(
  ref: DocumentReference<T> | string | undefined
): Promise<WithRef<T> | undefined> {
  if (!ref) return undefined;
  if (typeof ref === "string") {
    ref = getDocument<T>(ref);
  }
  return withRefOrUndefined(await getDoc(ref));
}

export const callChat = httpsCallable<ChatRequest, ChatResponse, ChatChunk>(
  functions,
  "chat"
);

export const getFunFacts = httpsCallable<FunFactsRequest, FunFactsResponse>(
  functions,
  "getFunFacts",
  { timeout: 120_000 }
);

export const getPersonalityQuestions = httpsCallable<
  PersonalityQuestionsRequest,
  PersonalityQuestionsResponse
>(functions, "getPersonalityQuestions", { timeout: 120_000 });

export const applyPersonalityHints = createEndpointCall<
  ApplyPersonalityHintsRequest,
  ApplyPersonalityHintsResponse
>(functions, "applyPersonalityHints", { timeout: 120_000 });

// Named `deleteCharacterFn` because stores expose a `deleteCharacter` action —
// keeping the names distinct avoids shadowing at import sites.
export const deleteCharacterFn = createEndpointCall<
  DeleteCharacterRequest,
  DeleteCharacterResponse
>(functions, "deleteCharacter", { timeout: 120_000 });

export const reassignJobAgent = createEndpointCall<
  ReassignJobAgentRequest,
  ReassignJobAgentResponse
>(functions, "reassignJobAgent", { timeout: 120_000 });

const clinicianFunction = createEndpointCall<
  ClinicianRequest,
  ClinicianResponseMap[keyof ClinicianResponseMap]
>(functions, "clinician");

export function clinicianApi<T extends ClinicianRequest>(
  request: T
): Promise<HttpsCallableResult<ClinicianResponseMap[T["action"]]>> {
  return clinicianFunction(request) as Promise<
    HttpsCallableResult<ClinicianResponseMap[T["action"]]>
  >;
}

const purchasesFunction = httpsCallable<
  PurchasesRequest,
  PurchasesResponseMap[keyof PurchasesResponseMap]
>(functions, "purchases");

export function purchasesApi<T extends PurchasesRequest>(
  request: T
): Promise<HttpsCallableResult<PurchasesResponseMap[T["action"]]>> {
  return purchasesFunction(request) as Promise<
    HttpsCallableResult<PurchasesResponseMap[T["action"]]>
  >;
}

const testManagerFunction = createEndpointCall<
  TestManagerRequest,
  TestManagerResponseMap[keyof TestManagerResponseMap]
>(functions, "testManager");

export function testManagerApi<T extends TestManagerRequest>(
  request: T
): Promise<HttpsCallableResult<TestManagerResponseMap[T["action"]]>> {
  return testManagerFunction(request) as Promise<
    HttpsCallableResult<TestManagerResponseMap[T["action"]]>
  >;
}

const manageFunction = httpsCallable<
  ManageRequest,
  ManageResponseMap[keyof ManageResponseMap]
>(functions, "manage");

// require admin permissions
export function manageApi<T extends ManageRequest>(
  request: T
): Promise<HttpsCallableResult<ManageResponseMap[T["action"]]>> {
  return manageFunction(request) as Promise<
    HttpsCallableResult<ManageResponseMap[T["action"]]>
  >;
}

// Emulators connected in firebaseCore.ts

setFirestoreContractProvider({
  getCollection,
  getDocument,
  getWithRef,
  getEnv,
  isEmulator,
  isProduction,
});

export const claimRoutineRewardFn = createEndpointCall<
  ClaimRoutineRewardRequest,
  RoutineRewardGoal
>(functions, "claimRoutineReward");

export const startRoutineRewardFn = createEndpointCall<
  StartRoutineRewardRequest,
  RoutineRewardGoal
>(functions, "startRoutineReward");

export const adminEquipItemFn = createEndpointCall<
  AdminEquipItemRequest,
  AdminEquipItemResponse
>(functions, "adminEquipItem");
export const changeOutfitFn = createEndpointCall<
  ChangeOutfitRequest,
  ChangeOutfitResponse
>(functions, "changeOutfit");
export const itemPreferenceFn = createEndpointCall<
  import("@um/contracts").ItemPreferenceRequest,
  import("@um/contracts").ItemPreferenceResponse
>(functions, "itemPreference");
export const editPersonaFn = createEndpointCall<
  EditPersonaRequest,
  { success: boolean }
>(functions, "editPersona");
export const photoWearableManagementFn = createEndpointCall<
  import("@um/contracts").EndpointRequestMap["photoWearableManagement"],
  import("@um/contracts").PhotoWearableStatus
>(functions, "photoWearableManagement", { timeout: 120_000 });
export const photoWearableFn = createEndpointCall<
  import("@um/contracts").PhotoWearableRequest,
  import("@um/contracts").PhotoWearableStatus
>(functions, "photoWearable", { timeout: 120_000 });

export const addWaitingListEntry = async (
  input: AddWaitingListEntryRequest
) => ({ data: await saveWaitingListEntry(db, input) });
